import { AttachPerms_ChangePermissionsFailed } from './changePermissionsContextMenuItemBuilder.locstring.json';
import { ChangePermissionsText } from 'owa-locstrings/lib/strings/changepermissionstext.locstring.json';
import loc from 'owa-localize';
import isMenuActionSupported from '../../utils/isMenuActionSupported';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { logUsage } from 'owa-analytics';
import getReferenceAttachmentPermissionString from 'owa-attachment-view/lib/utils/getReferenceAttachmentPermissionString';
import { AttachmentInfo, AttachmentModel } from 'owa-attachment-model-store';
import changeAttachmentPermission from 'owa-attachment-model-store/lib/actions/changeAttachmentPermission';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { confirm } from 'owa-confirm-dialog';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';

import {
    ActionMessageId,
    ActionType,
    AttachmentFullViewState,
    AttachmentMenuAction,
    setOngoingActionAndActionMessage,
} from 'owa-attachment-full-data';
import { trace } from 'owa-trace';

export default function changePermissionsContextMenuItemBuilder(
    attachmentViewState: AttachmentFullViewState,
    providerType: string
): IContextualMenuItem | null {
    if (
        !isMenuActionSupported(AttachmentMenuAction.ChangePermissions, attachmentViewState) ||
        attachmentViewState.isReadOnly
    ) {
        return null;
    }

    const attachmentModel = getAttachment(attachmentViewState.attachmentId);

    if (!attachmentModel || !attachmentModel.info) {
        return null;
    }

    if (
        !attachmentModel.info.supportedPermissionLevels ||
        attachmentModel.info.supportedPermissionLevels.length <= 1
    ) {
        return null;
    }

    const referenceAttachment = attachmentModel.model as ReferenceAttachment;

    // AttachmentPermissionType should only be none when the user does not have permission to share the file
    // or the file is still in the process of being attached. In either case we do not want to show this option.
    if (referenceAttachment.PermissionType === AttachmentPermissionLevel.None) {
        return null;
    }

    return {
        name: loc(ChangePermissionsText),
        key: 'ChangePermissions',
        items: getChangePermissionOptions(
            attachmentModel.info,
            providerType,
            attachmentViewState,
            referenceAttachment.PermissionType
        ),
    };
}

function getChangePermissionOptions(
    attachmentInfo: AttachmentInfo,
    providerType: string,
    attachmentViewState: AttachmentFullViewState,
    currentPermissionLevel: AttachmentPermissionLevel
): IContextualMenuItem[] {
    return attachmentInfo.supportedPermissionLevels.map(permLevel =>
        createPermissionMenuItem(
            permLevel,
            providerType,
            attachmentViewState,
            currentPermissionLevel
        )
    );
}

function createPermissionMenuItem(
    permissionLevel: AttachmentPermissionLevel,
    providerType: string,
    attachmentViewState: AttachmentFullViewState,
    currentPermissionLevel: AttachmentPermissionLevel
): IContextualMenuItem | null {
    if (permissionLevel === AttachmentPermissionLevel.None) {
        return null;
    }

    return {
        name: getReferenceAttachmentPermissionString(permissionLevel, providerType),
        key: permissionLevel.toString(),
        onClick: () => {
            onPermissionMenuItemClicked(
                permissionLevel,
                attachmentViewState,
                currentPermissionLevel,
                providerType
            );
        },
    };
}

async function onPermissionMenuItemClicked(
    newPermissionLevel: AttachmentPermissionLevel,
    attachmentViewState: AttachmentFullViewState,
    currentPermissionLevel: AttachmentPermissionLevel,
    providerType: string
): Promise<void> {
    const attachmentModel: AttachmentModel = getAttachment(attachmentViewState.attachmentId);
    logUsage('AttachmentContextMenuItemChangePermission', [
        providerType,
        attachmentModel.model.AttachmentId?.Id,
    ]);
    if (newPermissionLevel === currentPermissionLevel) {
        return;
    }

    try {
        // TODO 15210: Cache the sharing links as a perf optimization
        setOngoingActionAndActionMessage(
            attachmentViewState,
            ActionType.ChangePermission,
            ActionMessageId.ChangePermissionInProgress
        );
        await changeAttachmentPermission([attachmentViewState.attachmentId], newPermissionLevel);
    } catch (error) {
        trace.error(
            `changeAttachmentPermission error message: '${error.message}', stack: '${error.stack}'`
        );
        confirm('', loc(AttachPerms_ChangePermissionsFailed), false, {
            hideCancelButton: true,
        });
    }
    setOngoingActionAndActionMessage(attachmentViewState, ActionType.None, ActionMessageId.None);
}
